import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Datagrid, Icon, Button, Dropdown, DropdownOption } from "@abb/abb-common-ux-react";
import "./energy-cost-datagrid.css";
import PeriodList from '../../Period-Dropdown-list/period-dropdown-list';
import dataGrid from './energy-cost-datagrid.json';
import { BASE_URL } from '../../../strings';
import { getToken } from '../../Authentication/get-token';

const MemoizedTestComp = React.memo(({ i }) => (
    <>
        <Icon name="abb/alarm_bell" style={{ marginRight: '16px' }} onClick={() => alert(`Clicked icon on row ${i}`)} />
        <Button sizeClass="small" text={`Button ${i}`} onClick={() => alert(`Clicked button on row ${i}`)} />
    </>
));

const EngergyCostCmp = () => {
    const [monochrome, setMonochrome] = useState(false);
    const [rawData, setRawData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState(undefined);
    const [columnOrder, setColumnOrder] = useState(undefined);
    const [hideColumnFilters, setHideColumnFilters] = useState(false);
    const [columnFilters, setColumnFilters] = useState({});
    const [activeRow, setActiveRow] = useState(undefined);

    // Fetch and set data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${BASE_URL}emapi/energy_cost_comp`, getToken());
                setRawData(response.data.data || []);
                setColumns([
                    { fieldKey: 'Id', title: 'Group' },
                    { fieldKey: 'Value', title: 'Current Cost' },
                    { fieldKey: 'Prev', title: 'Previous Cost' }
                ]);
            } catch (error) {
                console.error("Error fetching energy cost data:", error);
            }
        };
        fetchData();
    }, []);

    // Memoized data transformation
    const data = useMemo(() => {
        return rawData.map((item, i) => ({
            rowId: `row-${i}`,
            fields: {
                Id: item.group,
                Value: item.current_cost,
                Prev: item.prev_cost,
                CustomContent: <MemoizedTestComp key={`row-${i}`} i={i} />
            }
        }));
    }, [rawData]);

    // Handlers
    const handleSelectRow = useCallback((rowIndex) => {
        setRawData(prev =>
            prev.map((item, i) => ({
                ...item,
                isSelected: rowIndex === i ? !item.isSelected : item.isSelected
            }))
        );
    }, []);

    const handleSelectAll = useCallback((oldValue, begin, end) => {
        setRawData(prev =>
            prev.map((item, i) => (
                i >= begin && i <= end && !item.disableSelect
                    ? { ...item, isSelected: oldValue !== 2 }
                    : item
            ))
        );
    }, []);

    const handleRowActivation = useCallback((rowIndex, rowId) => {
        setRawData(prev =>
            prev.map(item => ({ ...item, isActive: `row-${rowIndex}` === rowId }))
        );
        setActiveRow(data.find(row => row.rowId === rowId));
    }, [data]);

    const handleToggleRowExpansion = useCallback((expandedRows) => {
        setRawData(prev =>
            prev.map((item, i) => ({ ...item, isExpanded: expandedRows[i] === true }))
        );
    }, []);

    return (
        <div>
            <div className='indicator-container-line'>
                <PeriodList />
                {dataGrid.dropdowns.map((dropdown, index) => (
                    <Dropdown style={{ visibility: "hidden" }} key={index} label={dropdown.label} placeholder={dropdown.placeholder} monochrome={monochrome}>
                        {dropdown.options.map(option => (
                            <DropdownOption key={option.key} label={option.label} value={option.value} />
                        ))}
                    </Dropdown>
                ))}
            </div>

            <Datagrid
                id='energy-cost-compare'
                style={{ height: '235px', width: "88%", marginTop: '5px' }}
                monochrome={true}
                data={data}
                columns={columns}
                pageSize={pageSize}
                page={currentPage}
                rowHeight="small"
                zebraColoring={4}
                enableSorting={true}
                enableRowExpansion={false}
                enableRowActivation={true}
                enableColumnDragging={true}
                enableColumnResize={true}
                onToggleRowSelect={handleSelectRow}
                onToggleSelectAll={handleSelectAll}
                onActivateRow={handleRowActivation}
                onToggleRowExpansion={handleToggleRowExpansion}
                sortOrder={sortOrder}
                onSort={setSortOrder}
                columnOrder={columnOrder}
                onColumnOrderChange={setColumnOrder}
                onColumnWidthsChange={(newWidths) => {
                    setColumns(prev => prev.map((c) => ({ ...c, width: newWidths[c.fieldKey] })));
                }}
                onClickColumnFiltersIcon={() => setHideColumnFilters(prev => !prev)}
                onClickClearColumnFilters={() => {
                    const newFilters = { ...columnFilters };
                    Object.keys(newFilters).forEach(k => newFilters[k].value = '');
                    setColumnFilters(newFilters);
                    setHideColumnFilters(true);
                }}
                customMessage={data.length !== 0 ? undefined : 'Nothing in data'}
            />
        </div>
    );
};

export default EngergyCostCmp;
